import React, { useMemo } from 'react';

import { AppContextProvider } from '../../../store/App/store';
import { AppEndpoints } from '../../../types/app';
import { OcChatConfig, PreviewState } from '../../../types/oc';
import { MESSAGE_ROLE,Messages, MessageStatus } from "../../Message/types/messages";
import { initChatSession, resumeChatSession, sendChatMessage,sendMessageReaction, trackMessageCopy } from '../services/ocEndpoints';

export type AiAssistantContextProps = typeof AppContextProvider extends React.FC<infer Props> ? Props : never;
export type AiAssistantEndpoints = AiAssistantContextProps['endpoints'];

export const handleMessageReaction = (
    messageKey: string,
    status: MessageStatus,
    sessionKey: string,
    config: OcChatConfig
): Promise<boolean> => sendMessageReaction(config, messageKey, status, sessionKey);

export const handleMessageCopied = (messageId: string, sessionId: string, config: OcChatConfig): Promise<boolean> =>
    trackMessageCopy(config, messageId, sessionId);


export const useAiAssistantEndpoints = (
    config: OcChatConfig,
    onSendMessage?: (sessionKey: string, message: string) => void,
    processor: (r: Messages) => Messages | Promise<Messages> = (a) => a
): AiAssistantEndpoints => {
    return useMemo<AppEndpoints>(
        () => ({
            initChat: () => initChatSession(config).then(processor),
            resumeChat: (_, sessionKey) => resumeChatSession(config, sessionKey).then(processor),
            sendMessage: (message, sessionKey) => {
                // Call the callback before sending the message
                onSendMessage?.(sessionKey, message);
                return sendChatMessage(config, message, sessionKey).then(processor);
            },
            handleMessageCopied: (messageId, sessionId) => handleMessageCopied(messageId, sessionId, config),
            handleMessageReaction: (messageId, status, sessionId) => handleMessageReaction(messageId, status, sessionId, config)
        }),
        [config, onSendMessage, processor]
    );
};

/**
 * Mocked endpoints hook for preview mode
 * Returns endpoints that use mocked data instead of making real API calls
 */
export const useMockedAiAssistantEndpoints = (
    previewState: PreviewState,
    onSendMessage?: (sessionKey: string, message: string) => void,
    processor: (r: Messages) => Messages | Promise<Messages> = (a) => a
): AiAssistantEndpoints => {
    const sessionId = previewState.sessionId || 'preview-session-id';

    return useMemo<AppEndpoints>(
        () => {
            const mockedMessages = previewState.messages || [];
            
            return {
                initChat: async () => {
                    const messages: Messages = {
                        sessionId,
                        messages: mockedMessages,
                    };
                    return processor(messages);
                },
                resumeChat: async (_, sessionKey) => {
                    const messages: Messages = {
                        sessionId: sessionKey,
                        messages: mockedMessages,
                    };
                    return processor(messages);
                },
                sendMessage: async (message, sessionKey) => {
                    // Call the callback before sending the message
                    onSendMessage?.(sessionKey, message);

                    // In preview mode, just echo back the message with a simple response
                    const userMessage = {
                        role: MESSAGE_ROLE.USER,
                        message,
                        messageId: `preview-user-${Date.now()}`,
                        actionStates: { liked: null },
                    };

                    const assistantMessage = {
                        role: MESSAGE_ROLE.ASSISTANT,
                        message: `This is a preview mode response. You said: "${message}"`,
                        messageId: `preview-assistant-${Date.now()}`,
                        actionStates: { liked: null },
                    };

                    const messages: Messages = {
                        sessionId: sessionKey,
                        messages: [...mockedMessages, userMessage, assistantMessage],
                    };
                    return processor(messages);
                },
                handleMessageCopied: async () => {
                    // Mock success response
                    return Promise.resolve(true);
                },
                handleMessageReaction: async () => {
                    // Mock success response
                    return Promise.resolve(true);
                },
            };
        },
        [sessionId, previewState.messages, onSendMessage, processor]
    );
};

export const useDelayInitAiChatEndpoints = (
    config: OcChatConfig,
    onSendMessage?: (sessionKey: string, message: string) => void,
    processor: (r: Messages) => Messages | Promise<Messages> = (a) => a
): AiAssistantEndpoints => {
    return useAiAssistantEndpoints(config, onSendMessage, processor);
};
